// src/components/Presentation/ContentToolbar.js - Enhanced Version
import React, { useState } from 'react';
import { 
  Type, Image as ImageIcon, Video, BarChart3, Users, MessageCircle, Settings,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Palette,
  Plus, Minus, RotateCw, Crop, Maximize2, ChevronDown, ChevronUp
} from 'lucide-react';

const ContentToolbar = ({ 
  selectedContentType, 
  onContentTypeSelect, 
  selectedElement, 
  currentSlide, 
  onUpdateSlide 
}) => {
  const [showTextPanel, setShowTextPanel] = useState(false);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const contentTypes = [
    { type: 'text', name: 'Text', icon: Type, color: 'text-blue-600' },
    { type: 'image', name: 'Image', icon: ImageIcon, color: 'text-green-600' },
    { type: 'video', name: 'Video', icon: Video, color: 'text-red-600' },
    { type: 'chart', name: 'Chart', icon: BarChart3, color: 'text-purple-600' },
    { type: 'poll', name: 'Poll', icon: Users, color: 'text-orange-600' },
    { type: 'question', name: 'Q&A', icon: MessageCircle, color: 'text-indigo-600' }
  ];

  const fontSizes = [
    { value: 'text-xs', label: 'XS' },
    { value: 'text-sm', label: 'SM' },
    { value: 'text-base', label: 'MD' },
    { value: 'text-lg', label: 'LG' },
    { value: 'text-xl', label: 'XL' },
    { value: 'text-2xl', label: '2XL' },
    { value: 'text-3xl', label: '3XL' },
    { value: 'text-4xl', label: '4XL' },
    { value: 'text-5xl', label: '5XL' }
  ];

  const colorPresets = [
    '#000000', '#374151', '#6B7280', '#FFFFFF',
    '#EF4444', '#F97316', '#EAB308', '#22C55E',
    '#3B82F6', '#6366F1', '#A855F7', '#EC4899'
  ];

  const selectedSlate = currentSlide?.slates?.find(s => s.id === selectedElement);
  const showTextFormatting = selectedSlate?.type === 'text';
  const showImageFormatting = selectedSlate?.type === 'image';

  const updateSlateContent = (updates) => {
    if (!selectedSlate) return;
    
    const updatedSlate = {
      ...selectedSlate,
      content: { ...selectedSlate.content, ...updates }
    };
    
    const updatedSlates = currentSlide.slates.map(slate => 
      slate.id === selectedElement ? updatedSlate : slate
    );
    
    onUpdateSlide({ ...currentSlide, slates: updatedSlates });
  };

  const toggleTextFormat = (format) => {
    const content = selectedSlate.content || {};
    
    switch (format) {
      case 'bold':
        updateSlateContent({
          fontWeight: content.fontWeight === 'font-bold' ? 'font-normal' : 'font-bold'
        });
        break;
      case 'italic':
        updateSlateContent({
          fontStyle: content.fontStyle === 'italic' ? 'normal' : 'italic'
        });
        break;
      case 'underline':
        const hasUnderline = content.textDecoration?.includes('underline');
        updateSlateContent({
          textDecoration: hasUnderline ? 'none' : 'underline'
        });
        break;
    }
  };

  const adjustFontSize = (direction) => {
    const content = selectedSlate.content || {};
    const currentIndex = fontSizes.findIndex(size => size.value === (content.fontSize || 'text-lg'));
    let newIndex;
    
    if (direction === 'up') {
      newIndex = Math.min(fontSizes.length - 1, currentIndex + 1);
    } else {
      newIndex = Math.max(0, currentIndex - 1);
    }
    
    updateSlateContent({ fontSize: fontSizes[newIndex].value });
  };

  const adjustImageScale = (direction) => {
    const content = selectedSlate.content || {};
    const currentScale = content.scale || 100;
    const newScale = direction === 'up' 
      ? Math.min(300, currentScale + 10)
      : Math.max(10, currentScale - 10);
    
    updateSlateContent({ scale: newScale });
  };

  return (
    <div className="bg-white border-b border-gray-200 flex-shrink-0">
      {/* Main Toolbar */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Content Type Selection */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {contentTypes.map((contentType) => {
                const IconComponent = contentType.icon;
                const isSelected = selectedContentType === contentType.type;
                
                return (
                  <button
                    key={contentType.type}
                    onClick={() => onContentTypeSelect(isSelected ? null : contentType.type)}
                    className={`
                      flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all
                      ${isSelected 
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-900' 
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    <IconComponent className={`w-4 h-4 ${isSelected ? 'text-indigo-600' : contentType.color}`} />
                    <span className="text-sm font-medium">{contentType.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Element-Specific Controls */}
          <div className="flex items-center space-x-4">
            {/* Text Formatting Controls */}
            {showTextFormatting && (
              <div className="flex items-center space-x-2">
                {/* Quick Format Buttons */}
                <div className="flex items-center space-x-1 p-1 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => toggleTextFormat('bold')}
                    className={`p-2 rounded transition-colors ${
                      selectedSlate.content?.fontWeight === 'font-bold'
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Bold"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => toggleTextFormat('italic')}
                    className={`p-2 rounded transition-colors ${
                      selectedSlate.content?.fontStyle === 'italic'
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Italic"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => toggleTextFormat('underline')}
                    className={`p-2 rounded transition-colors ${
                      selectedSlate.content?.textDecoration?.includes('underline')
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Underline"
                  >
                    <Underline className="w-4 h-4" />
                  </button>
                </div>

                <div className="w-px h-6 bg-gray-300"></div>

                {/* Alignment */}
                <div className="flex items-center space-x-1 p-1 bg-gray-50 rounded-lg">
                  {['left', 'center', 'right'].map((align) => {
                    const AlignIcon = align === 'left' ? AlignLeft : align === 'center' ? AlignCenter : AlignRight;
                    return (
                      <button
                        key={align}
                        onClick={() => updateSlateContent({ alignment: align })}
                        className={`p-2 rounded transition-colors ${
                          (selectedSlate.content?.alignment || 'left') === align
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                        title={`Align ${align}`}
                      >
                        <AlignIcon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>

                <div className="w-px h-6 bg-gray-300"></div>

                {/* Font Size Controls */}
                <div className="flex items-center space-x-1 p-1 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => adjustFontSize('down')}
                    className="p-2 bg-white text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Decrease Font Size"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  
                  <select
                    value={selectedSlate.content?.fontSize || 'text-lg'}
                    onChange={(e) => updateSlateContent({ fontSize: e.target.value })}
                    className="px-2 py-1 text-sm border-0 bg-white rounded focus:ring-2 focus:ring-indigo-500"
                  >
                    {fontSizes.map((size) => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    onClick={() => adjustFontSize('up')}
                    className="p-2 bg-white text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Increase Font Size"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div className="w-px h-6 bg-gray-300"></div>

                {/* Color Picker */}
                <div className="relative">
                  <button
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className={`p-2 rounded transition-colors ${
                      showColorPicker
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Text Color"
                  >
                    <Palette className="w-4 h-4" />
                  </button>

                  {showColorPicker && (
                    <div className="absolute top-full right-0 mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {colorPresets.map((color) => (
                          <button
                            key={color}
                            onClick={() => {
                              updateSlateContent({ textColor: color });
                              setShowColorPicker(false);
                            }}
                            className={`w-8 h-8 rounded border-2 transition-all ${
                              selectedSlate.content?.textColor === color 
                                ? 'border-indigo-500 scale-110' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={selectedSlate.content?.textColor || '#000000'}
                          onChange={(e) => updateSlateContent({ textColor: e.target.value })}
                          className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={selectedSlate.content?.textColor || '#000000'}
                          onChange={(e) => updateSlateContent({ textColor: e.target.value })}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* More Text Options */}
                <button
                  onClick={() => setShowTextPanel(!showTextPanel)}
                  className={`p-2 rounded transition-colors ${
                    showTextPanel
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  title="More Text Options"
                >
                  {showTextPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            )}

            {/* Image Formatting Controls */}
            {showImageFormatting && (
              <div className="flex items-center space-x-2">
                {/* Image Fit Options */}
                <div className="flex items-center space-x-1 p-1 bg-gray-50 rounded-lg">
                  {[
                    { value: 'cover', icon: Crop, title: 'Cover' },
                    { value: 'contain', icon: Maximize2, title: 'Fit' },
                    { value: 'fill', icon: Maximize2, title: 'Fill' }
                  ].map((fit) => {
                    const FitIcon = fit.icon;
                    return (
                      <button
                        key={fit.value}
                        onClick={() => updateSlateContent({ objectFit: fit.value })}
                        className={`p-2 rounded transition-colors ${
                          (selectedSlate.content?.objectFit || 'cover') === fit.value
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                        title={fit.title}
                      >
                        <FitIcon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>

                <div className="w-px h-6 bg-gray-300"></div>

                {/* Image Scale Controls */}
                <div className="flex items-center space-x-1 p-1 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => adjustImageScale('down')}
                    className="p-2 bg-white text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Scale Down"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  
                  <span className="px-2 py-1 text-sm text-gray-600 min-w-12 text-center">
                    {selectedSlate.content?.scale || 100}%
                  </span>
                  
                  <button
                    onClick={() => adjustImageScale('up')}
                    className="p-2 bg-white text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Scale Up"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div className="w-px h-6 bg-gray-300"></div>

                {/* Image Rotation */}
                <button
                  onClick={() => {
                    const currentRotation = selectedSlate.content?.rotation || 0;
                    updateSlateContent({ rotation: currentRotation + 90 });
                  }}
                  className="p-2 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Rotate 90°"
                >
                  <RotateCw className="w-4 h-4" />
                </button>

                {/* More Image Options */}
                <button
                  onClick={() => setShowImagePanel(!showImagePanel)}
                  className={`p-2 rounded transition-colors ${
                    showImagePanel
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  title="More Image Options"
                >
                  {showImagePanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Extended Text Panel */}
      {showTextPanel && showTextFormatting && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-4 gap-6">
            {/* Font Family */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Font Family</label>
              <select
                value={selectedSlate.content?.fontFamily || 'font-sans'}
                onChange={(e) => updateSlateContent({ fontFamily: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
              >
                <option value="font-sans">Sans Serif</option>
                <option value="font-serif">Serif</option>
                <option value="font-mono">Monospace</option>
                <option value="font-inter">Inter</option>
                <option value="font-roboto">Roboto</option>
                <option value="font-poppins">Poppins</option>
              </select>
            </div>

            {/* Line Height */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Line Height: {((selectedSlate.content?.lineHeight || 1.5) * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="1"
                max="3"
                step="0.1"
                value={selectedSlate.content?.lineHeight || 1.5}
                onChange={(e) => updateSlateContent({ lineHeight: parseFloat(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Letter Spacing */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Letter Spacing: {selectedSlate.content?.letterSpacing || 0}px
              </label>
              <input
                type="range"
                min="-2"
                max="4"
                step="0.1"
                value={selectedSlate.content?.letterSpacing || 0}
                onChange={(e) => updateSlateContent({ letterSpacing: parseFloat(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Text Transform */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Transform</label>
              <select
                value={selectedSlate.content?.textTransform || 'none'}
                onChange={(e) => updateSlateContent({ textTransform: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
              >
                <option value="none">None</option>
                <option value="uppercase">UPPERCASE</option>
                <option value="lowercase">lowercase</option>
                <option value="capitalize">Capitalize</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Extended Image Panel */}
      {showImagePanel && showImageFormatting && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-4 gap-6">
            {/* Position Controls */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Horizontal: {selectedSlate.content?.position?.x || 50}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={selectedSlate.content?.position?.x || 50}
                onChange={(e) => updateSlateContent({
                  position: {
                    ...selectedSlate.content?.position,
                    x: parseInt(e.target.value)
                  }
                })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Vertical: {selectedSlate.content?.position?.y || 50}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={selectedSlate.content?.position?.y || 50}
                onChange={(e) => updateSlateContent({
                  position: {
                    ...selectedSlate.content?.position,
                    y: parseInt(e.target.value)
                  }
                })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Border Radius */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Corner Radius: {selectedSlate.content?.borderRadius || 0}px
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={selectedSlate.content?.borderRadius || 0}
                onChange={(e) => updateSlateContent({ borderRadius: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Opacity */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Opacity: {selectedSlate.content?.opacity || 100}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={selectedSlate.content?.opacity || 100}
                onChange={(e) => updateSlateContent({ opacity: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close color picker */}
      {showColorPicker && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowColorPicker(false)}
        />
      )}
    </div>
  );
};

export default ContentToolbar;